import { Profile, TenantCase, User } from "@happy-legal/graphql-types";
import {
  Box,
  Breadcrumbs,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import { CaseContext } from "../providers/CaseProvider";
import MUIRouterLink from "./MUIRouterLink";
import { capitalise } from "../utils";
import { StatusEnum } from "../enums";
import { getStatusVariant, resolveCaseType } from "../helpers";
import { CaseStatus } from "@happy-legal/graphql-enums";
import FormattedDateTimeTooltip from "./FormattedDateTimeTooltip";
import {
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "./table/StyledTable";

/**
 * Sub-line under the page title, shown only on a case detail view.
 *
 * The top bar already names the current section, so this carries just
 * what the title cannot: which case is open and whose it is. On any
 * page without a loaded case it renders nothing.
 */
const HeaderBreadcrumbs = () => {
  const caseContext = useContext(CaseContext);
  const state = caseContext?.state;
  const caseData = state?.caseData;
  const profile = caseData?.profile as any as Profile;

  if (!caseData) return null;

  // Filter current case id from other tenantCasesObjects via query string
  const otherCases =
    (profile?.tenantCasesObjects as any as TenantCase[])?.filter(
      (caseObject) => caseObject?.caseId !== caseData?.caseId,
    ) ?? [];

  const casePath = caseData?.path;
  const caseId = caseData?.caseId;
  const email = (profile?.user as any as User)?.email;
  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Breadcrumbs
        component="div"
        separator="›"
        sx={{
          display: "flex",
          alignItems: "center",
          "& .MuiBreadcrumbs-separator": {
            fontSize: "0.75rem",
            color: "text.disabled",
            mx: 0.75,
          },
          "& .MuiTypography-root, & a": {
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          },
          "& a": {
            color: "text.secondary",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          },
        }}
      >
        <MUIRouterLink to={`/${casePath}`} color="inherit">
          {capitalise(casePath ?? "")}
        </MUIRouterLink>
        <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
          Fall {caseId}
        </Typography>
        {(email || fullName) && (
          <Typography color="text.disabled">
            {email && (
              <Link
                href={`/users?sub=${profile?.id}`}
                color="inherit"
                underline="hover"
              >
                {email}
              </Link>
            )}
            {fullName && (
              <Box component="span" sx={{ ml: email ? 0.75 : 0 }}>
                {fullName}
              </Box>
            )}
          </Typography>
        )}
      </Breadcrumbs>

      {otherCases.length > 1 && <OtherCasesLink otherCases={otherCases} />}
    </Box>
  );
};

/**
 * Link to show other cases by same tenant
 */
const OtherCasesLink = ({ otherCases }: { otherCases: TenantCase[] }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <>
      <Link
        onClick={handleOpenDialog}
        sx={{
          flexShrink: 0,
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "text.secondary",
          textDecoration: "none",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          py: 0.125,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            backgroundColor: (theme) => theme.palette.surface.hover,
            borderColor: "primary.main",
            color: "primary.main",
          },
        }}
      >
        <span>{otherCases.length} weitere F&auml;lle</span>
      </Link>

      {dialogOpen && (
        <OtherCasesDialog open={dialogOpen} onClose={handleCloseDialog} otherCases={otherCases} />
      )}
    </>
  );
};

/**
 * Dialog for viewing other cases by same tenant
 */
const OtherCasesDialog = ({
  open,
  onClose,
  otherCases,
}: {
  open: boolean;
  onClose: () => void;
  otherCases: TenantCase[];
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Weitere Fälle dieses Mieters</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ border: 0, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Fall-Nr.", "Typ", "Status", "Erstellt am"].map((title) => (
                  <TableCell key={title}>{title}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {otherCases
                ?.slice()
                ?.sort(
                  (a, b) =>
                    Number(new Date(b!.created!)) - Number(new Date(a!.created!))
                )
                ?.map((otherCase, index) => {
                  const { label, path } = resolveCaseType(otherCase?.type);

                  return (
                    <TableRow key={otherCase?.id ?? `other-case-${index}`}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Link
                          sx={{ cursor: "pointer" }}
                          href={`${path}?id=${otherCase?.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {otherCase?.caseId}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {label}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          variant={getStatusVariant(otherCase?.status)}
                          status={
                            StatusEnum[otherCase?.status as CaseStatus] ?? "—"
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {otherCase?.created && (
                          <FormattedDateTimeTooltip date={otherCase.created} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export { HeaderBreadcrumbs };
